import { $http } from '@/bootstrap.js';
import { trans } from '@/mixins/i18n';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';

const FailureCodeType = {
    Known: 'KNOWN',
    Unknown: 'UNKNOWN',
    NotListed: 'NOT_LISTED',
} as const;

type FailureCodeType = (typeof FailureCodeType)[keyof typeof FailureCodeType];

type FailureCode = {
    id: string;
    label: string;
    type: FailureCodeType;
    [key: string]: unknown;
};

type ProcessStepStatus = 'open' | 'done' | 'active' | 'loading' | 'succeeded' | 'failed';

type ProcessStep = {
    value: string;
    label: string;
    status: ProcessStepStatus;
    loading?: boolean;
};

type ModalState =
    | 'Pending'
    | 'RestoringOperability'
    | 'PreventativeMaintenance'
    | 'CorrectiveMaintenanceWithoutIssueTriaging'
    | 'CorrectiveMaintenanceWithIssueTriaging'
    | 'CorrectiveMaintenanceWithKnownIssue'
    | 'CorrectiveMaintenanceWithUnknownIssue'
    | 'CorrectiveMaintenanceWithNotListedIssue';

export { FailureCodeType };
export type { FailureCode, ModalState };

export const useDiagnoseFieldIssueStore = defineStore('diagnoseFieldIssue', () => {
    const currentState = ref<ModalState>('Pending');
    const issueResolved = ref<boolean | null>(null);
    const workOrderId = ref<string | null>(null);
    const workOrderCode = ref<string | null>(null);
    const assetId = ref<string | null>(null);
    const assetOperability = ref<string | null>(null);
    const assetOperabilityChangedAt = ref<string | null>(null);
    const failureCodes = ref<FailureCode[] | null>(null);
    const selectedFailureCode = ref<FailureCode | null>(null);
    const processSteps = ref<ProcessStep[] | null>(null);
    const guidance = ref(null);
    const guidanceError = ref<string | null>(null);
    const failureCodesError = ref<string | null>(null);

    const hasFailureCodes = computed(() => failureCodes.value?.length > 0);

    const currentProcessStep = computed(() => {
        return (processSteps.value || []).find((step) => step.status === 'active')?.value ?? 'diagnosing';
    });

    const selectedMaintenanceType = computed({
        get: () => (currentState.value === 'PreventativeMaintenance' ? 'preventative_maintenance_in_progress' : 'corrective_maintenance_required'),
        set: (type: string) => selectMaintenanceType(type),
    });

    const isKnownFailure = computed(() => currentState.value === 'CorrectiveMaintenanceWithKnownIssue');

    const isUnknownOrNotListedFailure = computed(
        () => currentState.value === 'CorrectiveMaintenanceWithUnknownIssue' || currentState.value === 'CorrectiveMaintenanceWithNotListedIssue',
    );

    const showMaintenanceTypeToggle = computed(() => {
        const type = currentState.value;

        if (type === 'PreventativeMaintenance') return true;
        if (type === 'CorrectiveMaintenanceWithoutIssueTriaging') return true;
        if (type === 'CorrectiveMaintenanceWithIssueTriaging' && currentProcessStep.value === 'diagnosing') return true;

        return false;
    });

    function setup(props: {
        assetId: string;
        assetOperability: string;
        assetOperabilityChangedAt: string;
        guidance?: Record<string, unknown> | null;
        processSteps: ProcessStep[];
    }): void {
        reset();

        assetId.value = props.assetId;
        assetOperability.value = props.assetOperability;
        assetOperabilityChangedAt.value = props.assetOperabilityChangedAt;
        guidanceError.value = null;
        guidance.value = props.guidance ?? null;

        processSteps.value = props.processSteps;

        if (assetOperability.value === 'operable') {
            determineCurrentState();
        } else {
            void loadFailureCodes(props.assetId);
        }
    }

    async function loadFailureCodes(id: string): Promise<void> {
        failureCodes.value = null;
        failureCodesError.value = null;

        try {
            const response = await $http.get(route('assets.operability-triage.failure-codes', id));
            failureCodes.value = response.data;
            determineCurrentState();
        } catch (error: unknown) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            failureCodesError.value = status === 404 ? null : trans('fieldresolvableissue.issue_triage.failure_codes_error_fallback');

            failureCodes.value = [];
            determineCurrentState();
        }
    }

    function determineCurrentState() {
        if (assetOperability.value === 'operable') {
            currentState.value = 'RestoringOperability';
            return;
        }

        if (hasFailureCodes.value) {
            currentState.value = 'CorrectiveMaintenanceWithIssueTriaging';
        } else {
            currentState.value = 'CorrectiveMaintenanceWithoutIssueTriaging';
        }
    }

    function selectMaintenanceType(type: string): void {
        if (type === 'preventative_maintenance_in_progress') {
            currentState.value = 'PreventativeMaintenance';
            return;
        }

        if (hasFailureCodes.value) {
            currentState.value = 'CorrectiveMaintenanceWithIssueTriaging';
        } else {
            currentState.value = 'CorrectiveMaintenanceWithoutIssueTriaging';
        }
    }

    function selectFailureCode(code: FailureCode): void {
        selectedFailureCode.value = code;

        if (code.type === FailureCodeType.Unknown) {
            currentState.value = 'CorrectiveMaintenanceWithUnknownIssue';
        } else if (code.type === FailureCodeType.NotListed) {
            currentState.value = 'CorrectiveMaintenanceWithNotListedIssue';
        } else {
            currentState.value = 'CorrectiveMaintenanceWithKnownIssue';
        }
    }

    function clearFailureCode(): void {
        currentState.value = 'CorrectiveMaintenanceWithIssueTriaging';
        selectedFailureCode.value = null;
        guidance.value = null;
        guidanceError.value = null;
    }

    async function initiate(failureCode: FailureCode | null = null): Promise<{ workOrderId: string; workOrderCode: string }> {
        const response = await $http.post(route('assets.operability-triage.initiate', assetId.value), {
            operability: assetOperability.value,
            changed_at: assetOperabilityChangedAt.value,
            ...(failureCode ? { failure_code_id: failureCode.id } : {}),
        });

        workOrderId.value = response.data.workOrderId;
        workOrderCode.value = response.data.workOrderCode;

        return response.data;
    }

    function setLoadingStep(step: string | null = null) {
        if (!processSteps.value) {
            return;
        }

        processSteps.value = (processSteps.value || []).map((processStep) => ({
            ...processStep,
            loading: processStep.value === step,
        }));
    }

    async function getIssueGuidance(failureCode: FailureCode) {
        selectFailureCode(failureCode);
        guidanceError.value = null;
        setLoadingStep('troubleshooting');

        const workOrderPromise = workOrderId.value ? updateWorkOrderFailureCode(failureCode) : initiate(failureCode);

        try {
            const response = await $http.get(route('assets.operability-triage.guidance', assetId.value), {
                params: { failure_code_id: failureCode.id },
            });
            guidance.value = response.data.guidance;
            processSteps.value = response.data.processSteps;
        } catch (error: unknown) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            const message =
                status === 404
                    ? trans('fieldresolvableissue.issue_triage.guidance_error_not_found')
                    : trans('fieldresolvableissue.issue_triage.guidance_error_fallback');
            guidanceError.value = message;
        } finally {
            setLoadingStep(null);
        }

        await workOrderPromise;
    }

    function updateWorkOrderFailureCode(code: FailureCode) {
        return $http.patch(route('assets.operability-triage.update-failure-code', workOrderId.value), {
            failure_code_id: code.id,
        });
    }

    function reset(): void {
        currentState.value = 'Pending';
        issueResolved.value = null;
        workOrderId.value = null;
        workOrderCode.value = null;
        assetId.value = null;
        assetOperability.value = null;
        assetOperabilityChangedAt.value = null;
        failureCodes.value = null;
        failureCodesError.value = null;
        selectedFailureCode.value = null;
        processSteps.value = null;
        guidance.value = null;
        guidanceError.value = null;
    }

    return {
        assetId,
        assetOperability,
        assetOperabilityChangedAt,
        currentProcessStep,
        currentState,
        failureCodes,
        failureCodesError,
        guidance,
        guidanceError,
        isKnownFailure,
        isUnknownOrNotListedFailure,
        issueResolved,
        processSteps,
        selectedFailureCode,
        selectedMaintenanceType,
        showMaintenanceTypeToggle,
        workOrderCode,
        workOrderId,
        clearFailureCode,
        getIssueGuidance,
        initiate,
        loadFailureCodes,
        reset,
        selectMaintenanceType,
        setLoadingStep,
        setup,
    };
});
